import {
  assignSigninLiffIfLineWebView,
  isLiffLineMeGatewayHost,
  isSigninLiffClientEnv,
  loadLiffSdk,
  looksLikeLineEmbeddedBrowser,
  probeSigninClientEnvSync,
  readLiffSigninEnvProbe,
  resolveSigninClientEnvKindAfterLiffProbe,
  type SigninClientEnvKind,
} from "@/lib/openLineExternalBrowser";

export type SigninLineProfileWarmupResult = "redirected" | "warmed" | "skipped";

const SIGNIN_LIFF_PROFILE_BOOTSTRAP_KEY = "signin_liff_profile_bootstrap_v1";

function readSigninLiffProfileBootstrapDone(signinPath: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      sessionStorage.getItem(`${SIGNIN_LIFF_PROFILE_BOOTSTRAP_KEY}:${signinPath}`) ===
      "1"
    );
  } catch {
    return false;
  }
}

function markSigninLiffProfileBootstrapDone(signinPath: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(`${SIGNIN_LIFF_PROFILE_BOOTSTRAP_KEY}:${signinPath}`, "1");
  } catch {
    // ignore
  }
}

export type SigninLineProfileView = {
  name: string;
  pictureUrl?: string;
};

type SigninLiffSdk = {
  init: (config: {
    liffId: string;
    withLoginOnExternalBrowser?: boolean;
  }) => Promise<void>;
  ready?: Promise<unknown>;
  isInClient?: () => boolean;
  getContext?: () => { type?: string } | null;
  isLoggedIn?: () => boolean;
  getProfile?: () => Promise<{
    userId: string;
    displayName: string;
    pictureUrl?: string;
  }>;
  getDecodedIDToken?: () => Promise<{
    name?: string;
    picture?: string;
    sub?: string;
  }>;
};

let signinLiffReadyPromise: Promise<{
  liff: SigninLiffSdk;
  envKind: SigninClientEnvKind;
  inClient: boolean;
}> | null = null;

function getSigninLiffId(): string {
  return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN || "").trim();
}

/** Single LIFF init for /signin — avoids double `init` from env probe + profile hydrate. */
export function ensureSigninLiffReady(): Promise<{
  liff: SigninLiffSdk;
  envKind: SigninClientEnvKind;
  inClient: boolean;
}> {
  if (signinLiffReadyPromise) return signinLiffReadyPromise;

  signinLiffReadyPromise = (async () => {
    const liffId = getSigninLiffId();
    if (!liffId.includes("-")) {
      throw new Error("NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN is not configured");
    }
    const liff = (await loadLiffSdk()) as SigninLiffSdk;
    await liff.init({ liffId, withLoginOnExternalBrowser: true });
    if (liff.ready) await liff.ready;
    const probe = readLiffSigninEnvProbe(liff);
    return {
      liff,
      envKind: resolveSigninClientEnvKindAfterLiffProbe(
        probe.inClient,
        probe.contextType,
      ),
      inClient: probe.inClient,
    };
  })();

  signinLiffReadyPromise.catch(() => {
    signinLiffReadyPromise = null;
  });

  return signinLiffReadyPromise;
}

/** Clear cached init — e.g. after failed LIFF login redirect so retry can re-init. */
export function resetSigninLiffReady(): void {
  signinLiffReadyPromise = null;
}

export function readCachedSigninLineProfile(): SigninLineProfileView | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("line_profile");
    if (!raw?.trim()) return null;
    const parsed = JSON.parse(raw) as {
      displayName?: string;
      name?: string;
      pictureUrl?: string;
    };
    const name =
      (typeof parsed.displayName === "string" ? parsed.displayName.trim() : "") ||
      (typeof parsed.name === "string" ? parsed.name.trim() : "");
    if (!name) return null;
    return {
      name,
      pictureUrl:
        typeof parsed.pictureUrl === "string" ? parsed.pictureUrl : undefined,
    };
  } catch {
    try {
      localStorage.removeItem("line_profile");
    } catch {
      // ignore
    }
    return null;
  }
}

export function persistSigninLineProfile(p: {
  userId: string;
  displayName: string;
  pictureUrl?: string;
}): void {
  try {
    localStorage.setItem(
      "line_profile",
      JSON.stringify({
        userId: p.userId,
        displayName: p.displayName,
        pictureUrl: p.pictureUrl,
      }),
    );
    localStorage.setItem("line_user_id", p.userId);
  } catch {
    // ignore
  }
}

/** LIFF session can be ready shortly after redirect — mirror `/login/aera` retry. */
async function waitForSigninLiffLoggedIn(
  liff: SigninLiffSdk,
  opts?: { attempts?: number; delayMs?: number },
): Promise<boolean> {
  const attempts = opts?.attempts ?? 8;
  const delayMs = opts?.delayMs ?? 250;
  for (let i = 0; i < attempts; i++) {
    try {
      if (liff.isLoggedIn?.()) return true;
    } catch {
      // ignore
    }
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return false;
}

/** LIFF mini app: getProfile; fallback decoded ID token when isLoggedIn is false. */
export async function fetchSigninLineProfileFromLiff(
  liff: SigninLiffSdk,
): Promise<(SigninLineProfileView & { userId?: string }) | null> {
  const fromProfile = async (): Promise<{
    userId: string;
    displayName: string;
    pictureUrl?: string;
  } | null> => {
    if (typeof liff.getProfile !== "function") return null;
    try {
      return await liff.getProfile();
    } catch {
      return null;
    }
  };

  const profileViewFromRaw = (p: {
    userId: string;
    displayName: string;
    pictureUrl?: string;
  }): (SigninLineProfileView & { userId?: string }) | null => {
    if (!p.displayName?.trim()) return null;
    return {
      userId: p.userId,
      name: p.displayName.trim(),
      pictureUrl: p.pictureUrl,
    };
  };

  await waitForSigninLiffLoggedIn(liff);

  try {
    if (liff.isLoggedIn?.()) {
      const p = await fromProfile();
      const view = p ? profileViewFromRaw(p) : null;
      if (view) return view;
    }
  } catch {
    // ignore
  }

  try {
    const token = await liff.getDecodedIDToken?.();
    const name = token?.name?.trim();
    if (name) {
      return {
        userId: token?.sub,
        name,
        pictureUrl:
          typeof token?.picture === "string" ? token.picture : undefined,
      };
    }
  } catch {
    // ignore
  }

  if (liff.isInClient?.()) {
    const p = await fromProfile();
    const view = p ? profileViewFromRaw(p) : null;
    if (view) return view;
  }

  return null;
}

/** Probe LIFF mini app vs gateway vs browser — same as `/go/billlin` and sheets reconnect. */
export async function probeSigninLiffClientEnvKind(): Promise<SigninClientEnvKind> {
  const syncKind = probeSigninClientEnvSync();
  if (syncKind !== null) return syncKind;

  if (!looksLikeLineEmbeddedBrowser() && !isLiffLineMeGatewayHost()) {
    return "browser";
  }

  try {
    const { envKind } = await ensureSigninLiffReady();
    return envKind;
  } catch {
    return resolveSigninClientEnvKindAfterLiffProbe(false);
  }
}

/**
 * `/go/billlin` และ `/signin?force=true&auto=1` — เปิดผ่าน signin LIFF endpoint ก่อน แล้ว prefetch
 * (in-place จาก LIFF อื่นมัก init ผิด LIFF ID → หาโปรไฟล์ไม่เจอ)
 *
 * อยู่บน `/signin` แล้ว → prefetch อย่างเดียว (กันเด้ง liff.line.me วน)
 */
export async function warmSigninLineProfileLikeBilllinGo(
  signinPath: string,
): Promise<SigninLineProfileWarmupResult> {
  if (typeof window === "undefined") return "skipped";

  const envKind = await probeSigninLiffClientEnvKind();
  if (!isSigninLiffClientEnv(envKind)) return "skipped";

  const rel = signinPath.startsWith("/") ? signinPath : `/${signinPath}`;
  const onSigninRoot =
    window.location.pathname === "/signin" && rel.startsWith("/signin");

  if (onSigninRoot) {
    resetSigninLiffReady();
    try {
      const warmed = await prefetchSigninLineProfileCache();
      if (warmed) return "warmed";
    } catch {
      // ignore — may need one liff.line.me bootstrap below
    }

    // Prefetch ล้มเหลว (เช่น in-place จาก LIFF อื่น) — bootstrap ผ่าน signin LIFF ครั้งเดียว
    if (!readSigninLiffProfileBootstrapDone(rel) && assignSigninLiffIfLineWebView(rel)) {
      markSigninLiffProfileBootstrapDone(rel);
      return "redirected";
    }

    return "skipped";
  }

  resetSigninLiffReady();

  if (assignSigninLiffIfLineWebView(rel)) return "redirected";

  try {
    const warmed = await prefetchSigninLineProfileCache();
    return warmed ? "warmed" : "skipped";
  } catch {
    return "skipped";
  }
}

// Keep UI responsive: warmup should not block connect UI too long.
export const SIGNIN_LIFF_PROFILE_WARMUP_TIMEOUT_MS = 6_000;

export async function warmSigninLineProfileWithTimeout(
  signinPath: string,
  timeoutMs = SIGNIN_LIFF_PROFILE_WARMUP_TIMEOUT_MS,
): Promise<SigninLineProfileWarmupResult> {
  return Promise.race([
    warmSigninLineProfileLikeBilllinGo(signinPath),
    new Promise<SigninLineProfileWarmupResult>((resolve) => {
      setTimeout(() => resolve("skipped"), timeoutMs);
    }),
  ]);
}

/** เรียกจาก /go/billlin ก่อนไป /signin — cache โปรไฟล์ให้ปุ่ม LINE แสดงทันที */
export async function prefetchSigninLineProfileCache(): Promise<boolean> {
  try {
    const { liff } = await ensureSigninLiffReady();
    const profile = await fetchSigninLineProfileFromLiff(liff);
    if (!profile?.name?.trim()) return false;
    if (profile.userId) {
      persistSigninLineProfile({
        userId: profile.userId,
        displayName: profile.name,
        pictureUrl: profile.pictureUrl,
      });
    } else {
      localStorage.setItem(
        "line_profile",
        JSON.stringify({
          displayName: profile.name,
          pictureUrl: profile.pictureUrl,
        }),
      );
    }
    return true;
  } catch {
    return false;
  }
}
